import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { LoggerService } from '../../logger.service';
import { PostService } from 'src/app/post.service';
import { Post } from 'src/app/types/post.type';

@Component({
  selector: 'app-post-list',
  templateUrl: './post-list.component.html',
  styleUrls: ['./post-list.component.scss'],
})
export class PostListComponent implements OnInit {
  posts: Post[] = [];
  searchTerm = '';
  errorMessage = '';

  fetching = false;
  creating = false;
  deletingPostId = '';

  constructor(
    private logger: LoggerService,
    private postService: PostService,
    private router: Router
  ) {}

  get filteredPosts(): Post[] {
    const keyword = this.searchTerm.trim().toLowerCase();

    if (!keyword) {
      return this.posts;
    }

    return this.posts.filter((post) => {
      const searchableText = [
        post.title,
        post.description,
        post.content,
        ...(post.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(keyword);
    });
  }

  get publishedCount(): number {
    return this.posts.filter((post) => post.is_published).length;
  }

  get draftCount(): number {
    return this.posts.length - this.publishedCount;
  }

  get hasPosts(): boolean {
    return this.posts.length > 0;
  }

  get isBusy(): boolean {
    return this.fetching || this.creating || !!this.deletingPostId;
  }

  trackByPostId(index: number, post: Post) {
    return post.post_id || index;
  }

  fetchList() {
    if (this.fetching) return;

    this.fetching = true;
    this.errorMessage = '';
    this.postService
      .getPosts()
      .pipe(finalize(() => (this.fetching = false)))
      .subscribe(
        (res) => {
          this.logger.log(res);
          this.posts = res || [];
        },
        (error) => {
          this.logger.log(error);
          this.errorMessage = '文章列表加载失败，请稍后重试。';
        }
      );
  }

  deletePost(event: MouseEvent, post: Post) {
    event.stopPropagation();

    if (!post.post_id || this.deletingPostId) return;

    const title = post.title || '未命名文章';
    const confirmed = window.confirm(
      `确定删除「${title}」吗？此操作不可恢复。`
    );

    if (!confirmed) return;

    this.deletingPostId = post.post_id;
    this.errorMessage = '';
    this.postService
      .deletePost(post.post_id)
      .pipe(finalize(() => (this.deletingPostId = '')))
      .subscribe(
        (res) => {
          this.logger.log(res);
          this.posts = this.posts.filter(
            (item) => item.post_id !== post.post_id
          );
        },
        (error) => {
          this.logger.log(error);
          this.errorMessage = '删除文章失败，请刷新后重试。';
        }
      );
  }

  handleSearch(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
  }

  clearSearch() {
    this.searchTerm = '';
  }

  handlePostCardClick(post_id?: string) {
    if (!post_id) return;
    this.editPost(post_id);
  }

  newPost() {
    if (this.creating) return;

    this.creating = true;
    this.errorMessage = '';
    this.postService
      .createPost({
        title: 'New Post Title',
        content: '',
        description: '',
        theme: 'default',
        draft: '',
      })
      .pipe(finalize(() => (this.creating = false)))
      .subscribe(
        (res) => {
          this.logger.log(res);
          if (res.post_id) {
            this.posts = [res, ...this.posts];
            this.editPost(res.post_id);
          }
        },
        (error) => {
          this.logger.log(error);
          this.errorMessage = '新建文章失败，请稍后重试。';
        }
      );
  }

  editPost(post_id: string) {
    this.router.navigate(['/edit', post_id]);
  }

  ngOnInit(): void {
    this.fetchList();
  }
}
